import { firestore } from "./firebase";
import {
  collection,
  collectionGroup,
  doc,
  FirestoreError,
  getDoc,
  limit as firestoreLimit,
  onSnapshot,
  orderBy,
  query,
  QueryConstraint,
  startAfter,
  Unsubscribe,
  where,
} from "firebase/firestore";
import {
  COLLECTION_ID,
  MEMBERS_COLLECTION_ID,
  SPACE_MEMBER_TYPE,
  SpaceMember,
} from "../core/entities";

const membersCollection = (spaceId: Id) =>
  collection(firestore, COLLECTION_ID.SPACE, spaceId, MEMBERS_COLLECTION_ID);

/**
 * Watches every member doc for one identity, across every space: the global
 * my-memberships stream. It backs the permission engine, the Library space
 * list, and cooperative revocation teardown. In that teardown, the client
 * itself detaches from a space when a revocation removes its member doc.
 *
 * The query is deliberately unordered. An ordered query needs a second
 * composite index for no gain: every consumer either indexes the result by
 * `spaceId` or sorts client-side.
 *
 * `isSaturated` is true when the result set is at `limit`. A saturated stream
 * cannot tell "your access was revoked" apart from "another doc pushed this
 * one out of the window". So the caller must suppress cooperative teardown
 * and fall back to point reads. See MY_MEMBERS_LIMIT.
 */
export const watchMyMembers: (props: {
  memberId: Id;
  limit: number;
  onChange: (props: {
    members: SpaceMember[];
    removedSpaceIds: Id[];
    isSaturated: boolean;
  }) => void;
  onError?: (error: FirestoreError) => void;
}) => Unsubscribe = ({ memberId, limit, onChange, onError }) => {
  const membersQuery = query(
    collectionGroup(firestore, MEMBERS_COLLECTION_ID),
    where("memberId", "==", memberId),
    firestoreLimit(limit),
  );

  return onSnapshot(
    membersQuery,
    (snapshot) => {
      const members = snapshot.docs.map((s) => s.data() as SpaceMember);

      /* Firestore reports every doc in the first snapshot as "added", so this
       * list is empty on boot and never looks like a mass revocation. */
      const removedSpaceIds = snapshot
        .docChanges()
        .filter((change) => change.type === "removed")
        .map((change) => (change.doc.data() as SpaceMember).spaceId)
        .filter((spaceId) => !!spaceId);

      onChange({
        members,
        removedSpaceIds,
        isSaturated: snapshot.size >= limit,
      });
    },
    (error) => {
      console.log(`Could not watch members for [${memberId}]:`, error);
      onError && onError(error);
    },
  );
};

/**
 * Watches my member docs whose spaces are roots in my sidebar, in the order
 * the sidebar renders them. `isTopLevel` means "can read, with nothing
 * inherited", which is exactly what `topLevelReadableBy` encoded, and
 * `spaceName` is a byte-for-byte copy of `space.name`. So this reproduces
 * `where(topLevelReadableBy array-contains-any [me]) orderBy(name asc)`.
 */
export const watchMyTopLevelMembers: (props: {
  memberId: Id;
  limit: number;
  onChange: (members: SpaceMember[]) => void;
  onError?: (error: FirestoreError) => void;
}) => Unsubscribe = ({ memberId, limit, onChange, onError }) => {
  const membersQuery = query(
    collectionGroup(firestore, MEMBERS_COLLECTION_ID),
    where("memberId", "==", memberId),
    where("isTopLevel", "==", true),
    orderBy("spaceName", "asc"),
    firestoreLimit(limit),
  );

  return onSnapshot(
    membersQuery,
    (snapshot) => {
      onChange(snapshot.docs.map((s) => s.data() as SpaceMember));
    },
    (error) => {
      console.log(
        `Could not watch top level members for [${memberId}]:`,
        error,
      );
      onError && onError(error);
    },
  );
};

/**
 * Watches one page of a space's roster.
 *
 * `startAfterMember` is the last member of the previous page. With a single
 * explicit `orderBy('addedAt','desc')`, Firestore appends an implicit
 * `orderBy(__name__,'desc')`, and the member doc id is `memberId`. So the
 * pair `[addedAt, memberId]` is a complete, stable cursor.
 *
 * Firestore drops a member doc with no `addedAt` from this query, with no
 * error. `useSpaceRoster` carries a canary (a check that raises an alert) for
 * that case.
 */
export const watchSpaceRoster: (props: {
  spaceId: Id;
  type: SPACE_MEMBER_TYPE;
  limit: number;
  startAfterMember?: SpaceMember;
  onChange: (members: SpaceMember[]) => void;
  onError?: (error: FirestoreError) => void;
}) => Unsubscribe = ({
  spaceId,
  type,
  limit,
  startAfterMember,
  onChange,
  onError,
}) => {
  const constraints: QueryConstraint[] = [
    where("type", "==", type),
    orderBy("addedAt", "desc"),
  ];

  if (startAfterMember) {
    constraints.push(
      startAfter(startAfterMember.addedAt, startAfterMember.memberId),
    );
  }

  constraints.push(firestoreLimit(limit));

  return onSnapshot(
    query(membersCollection(spaceId), ...constraints),
    (snapshot) => {
      onChange(snapshot.docs.map((s) => s.data() as SpaceMember));
    },
    (error) => {
      console.log(`Could not watch roster for Space [${spaceId}]:`, error);
      onError && onError(error);
    },
  );
};

/**
 * Watches the `public` and `share_*` member docs of one space: the
 * share-link state. The set stays small in practice (one `public` doc
 * exists, and the Share dialog cleans old keys up), so one listener with no
 * paging is enough.
 */
export const watchSpaceAccessMembers: (props: {
  spaceId: Id;
  onChange: (members: SpaceMember[]) => void;
  onError?: (error: FirestoreError) => void;
}) => Unsubscribe = ({ spaceId, onChange, onError }) => {
  const membersQuery = query(
    membersCollection(spaceId),
    where("type", "in", [SPACE_MEMBER_TYPE.SHARE, SPACE_MEMBER_TYPE.PUBLIC]),
  );

  return onSnapshot(
    membersQuery,
    (snapshot) => {
      onChange(snapshot.docs.map((s) => s.data() as SpaceMember));
    },
    (error) => {
      console.log(
        `Could not watch access members for Space [${spaceId}]:`,
        error,
      );
      onError && onError(error);
    },
  );
};

/**
 * Reads one (space, identity) member doc. The permission engine uses it when
 * the my-memberships stream is saturated. It also uses it for a share-key
 * identity, which has no stream at all: the rules deny the collection-group
 * form of that query. With this read, the permission engine can still answer
 * for a space outside the stream's window. It costs 1 document read and
 * 0 rule document accesses: the rule compares `memberId` on the doc under
 * evaluation.
 */
export const getSpaceMember = async (props: {
  spaceId: Id;
  memberId: Id;
}): Promise<SpaceMember | null> => {
  const snapshot = await getDoc(
    doc(
      firestore,
      COLLECTION_ID.SPACE,
      props.spaceId,
      MEMBERS_COLLECTION_ID,
      props.memberId,
    ),
  );

  if (!snapshot.exists()) return null;
  return snapshot.data() as SpaceMember;
};
